/**
 * The top bar's global search (change spec 2026-09-24/topbar, design 1): the row shape of
 * `portal_search` and where each hit opens. Pure, so the mapping is tested without a screen.
 *
 * A load opens its own page. Carriers, drivers, trucks and brokers have no page of their own yet: they
 * open their list filtered to the hit's name (`?q=`), every status (`status=all`), so an inactive
 * carrier is still found. The lists already keep `q` and `status` in the URL (listParams.ts).
 */

export const SEARCH_KINDS = ['load', 'driver', 'carrier', 'truck', 'broker'] as const;
export type SearchKind = (typeof SEARCH_KINDS)[number];

/** Under this many characters the server searches nothing (and loads need three, server side). */
export const MIN_QUERY = 2;

export interface SearchHit {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle: string | null;
  status: string | null;
  /** The field that matched best: number, reference, city, name, phone, dba, mc, dot, unit, vin, plate. */
  matched: string;
  score: number;
}

/** The query to send, or null when there is nothing to ask. */
export function normalizeQuery(q: string): string | null {
  const t = q.trim();
  return t.length >= MIN_QUERY ? t : null;
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

export function toSearchHit(r: Record<string, unknown>): SearchHit | null {
  const kind = r.kind;
  if (typeof kind !== 'string' || !(SEARCH_KINDS as readonly string[]).includes(kind)) return null;
  return {
    kind: kind as SearchKind,
    id: String(r.id),
    title: str(r.title) ?? '',
    subtitle: str(r.subtitle),
    status: str(r.status),
    matched: str(r.matched) ?? '',
    score: Number(r.score ?? 0),
  };
}

function listHref(path: string, q: string, allStatuses: boolean): string {
  const params = new URLSearchParams({ q });
  if (allStatuses) params.set('status', 'all');
  return `${path}?${params.toString()}`;
}

export function searchHref(hit: SearchHit): string {
  switch (hit.kind) {
    case 'load':
      return `/loads/${hit.id}`;
    case 'carrier':
      return listHref('/carriers', hit.title, true);
    case 'driver':
      return listHref('/carriers/drivers', hit.title, true);
    case 'truck':
      return listHref('/carriers/trucks', hit.title, true);
    case 'broker':
      return listHref('/brokers', hit.title, false);
  }
}
